import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip,
  Avatar,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Loader from '../../components/Loader';
import { MdAttachMoney } from 'react-icons/md';
import { FaWallet } from 'react-icons/fa';
import { HiUsers } from 'react-icons/hi';
import { AiOutlineWarning } from 'react-icons/ai';
import {
  getCompanyCredits,
  allocateCredits,
  CompanyCreditsResponse,
  AgentCreditInfo,
} from '../../services/company-credits';
import { reduceAgentCredits } from '../../services/agent-credits';
import { getAgentsList, AgentListItem } from '../../services/agent-management';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { toastHandler } from '../../utils/toastHandler';
import moment from 'moment';

// Validation schemas
const allocateSchema = Yup.object().shape({
  agentId: Yup.string().required('Agent is required'),
  credits: Yup.number()
    .min(1, 'Credits must be at least 1')
    .required('Credits amount is required'),
});

const reduceSchema = Yup.object().shape({
  agentId: Yup.string().required('Agent is required'),
  credits: Yup.number()
    .min(1, 'Credits must be at least 1')
    .required('Credits amount is required'),
});

const CreditManagement = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CompanyCreditsResponse | null>(null);
  const [allocateModalOpen, setAllocateModalOpen] = useState(false);
  const [reduceModalOpen, setReduceModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentCreditInfo | null>(null);
  const [agentsList, setAgentsList] = useState<AgentListItem[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getCompanyCredits();
      setData(response);
    } catch (error: any) {
      console.error('Error fetching credits:', error);
      toastHandler(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch agents list
  const fetchAgentsList = async () => {
    try {
      setLoadingAgents(true);
      const response = await getAgentsList({ limit: 100 });
      setAgentsList(response.data || []);
    } catch (error: any) {
      console.error('Error fetching agents list:', error);
      toastHandler(error);
    } finally {
      setLoadingAgents(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchAgentsList();
  }, []);

  // Allocate form
  const allocateFormik = useFormik({
    initialValues: {
      agentId: '',
      credits: '',
    },
    validationSchema: allocateSchema,
    onSubmit: async (values) => {
      try {
        await allocateCredits({
          agentId: values.agentId,
          credits: parseInt(values.credits, 10),
        });
        toast.success('Credits allocated successfully');
        setAllocateModalOpen(false);
        allocateFormik.resetForm();
        fetchData();
      } catch (error: any) {
        toastHandler(error);
      }
    },
  });

  // Reduce form
  const reduceFormik = useFormik({
    initialValues: {
      agentId: '',
      credits: '',
    },
    validationSchema: reduceSchema,
    onSubmit: async (values) => {
      try {
        await reduceAgentCredits(values.agentId, {
          credits: parseInt(values.credits, 10),
        });
        toast.success('Credits reduced successfully');
        setReduceModalOpen(false);
        reduceFormik.resetForm();
        setSelectedAgent(null);
        fetchData();
      } catch (error: any) {
        toastHandler(error);
      }
    },
  });

  const handleAllocateClick = (agent?: AgentCreditInfo) => {
    if (agent) {
      allocateFormik.setFieldValue('agentId', agent.agentId);
    }
    setAllocateModalOpen(true);
  };

  const handleReduceClick = (agent: AgentCreditInfo) => {
    setSelectedAgent(agent);
    reduceFormik.setFieldValue('agentId', agent.agentId);
    setReduceModalOpen(true);
  };

  const getDaysUntilExpiration = (expiresAt: string): number => {
    const today = moment().startOf('day');
    const expiration = moment(expiresAt).startOf('day');
    return expiration.diff(today, 'days');
  };

  const getExpirationColor = (days: number): 'default' | 'warning' | 'error' => {
    if (days < 0) return 'error';
    if (days <= 7) return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Page title="Credit Management">
        <Container>
          <Loader />
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Credit Management">
      <Container maxWidth="xl">
        <Grid container spacing={2} sx={{ pt: 3 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ pb: 1 }}>
          <Typography variant="h4">Credit Management</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => handleAllocateClick()}
            disabled={!data || data.companyWallet.remainingInWallet <= 0}
          >
            Allocate Credits
          </Button>
          </Grid>
        </Grid>

        {/* Wallet Overview - Dashboard Style Cards */}
        <Box sx={{ pt: 3, mb: 3 }}>
          <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent
                sx={{ paddingTop: '16px', paddingBottom: '16px', paddingX: '20px' }}
              >
                <Stack
                  alignItems="flex-start"
                  direction="row"
                  justifyContent="space-between"
                  spacing={3}
                >
                  <Stack spacing={1}>
                    <Typography
                      color="text.secondary"
                      variant="overline"
                      sx={{ fontSize: '1rem' }}
                    >
                  Total Purchased
                </Typography>
                    <Typography variant="h4" color="primary.main">{data?.companyWallet.totalPurchased || 0}</Typography>
                  </Stack>
                  <Avatar
                    sx={{
                      backgroundColor: 'primary.main',
                      height: 56,
                      width: 56,
                    }}
                  >
                    <MdAttachMoney size={28} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent
                sx={{ paddingTop: '16px', paddingBottom: '16px', paddingX: '20px' }}
              >
                <Stack
                  alignItems="flex-start"
                  direction="row"
                  justifyContent="space-between"
                  spacing={3}
                >
                  <Stack spacing={1}>
                    <Typography
                      color="text.secondary"
                      variant="overline"
                      sx={{ fontSize: '1rem' }}
                    >
                      Total expired credits
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {data?.companyWallet.totalExpiredCredits ?? 0}
                    </Typography>
                  </Stack>
                  <Avatar
                    sx={{
                      backgroundColor: 'warning.main',
                      height: 56,
                      width: 56,
                    }}
                  >
                    <AiOutlineWarning size={28} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent
                sx={{ paddingTop: '16px', paddingBottom: '16px', paddingX: '20px' }}
              >
                <Stack
                  alignItems="flex-start"
                  direction="row"
                  justifyContent="space-between"
                  spacing={3}
                >
                  <Stack spacing={1}>
                    <Typography
                      color="text.secondary"
                      variant="overline"
                      sx={{ fontSize: '1rem' }}
                    >
                      Total used by agents
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {data?.companyWallet.totalUsedByAgents ?? 0}
                    </Typography>
                  </Stack>
                  <Avatar
                    sx={{
                      backgroundColor: 'error.main',
                      height: 56,
                      width: 56,
                    }}
                  >
                    <HiUsers size={28} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent
                sx={{ paddingTop: '16px', paddingBottom: '16px', paddingX: '20px' }}
              >
                <Stack
                  alignItems="flex-start"
                  direction="row"
                  justifyContent="space-between"
                  spacing={3}
                >
                  <Stack spacing={1}>
                    <Typography
                      color="text.secondary"
                      variant="overline"
                      sx={{ fontSize: '1rem' }}
                    >
                      Total used by company
                </Typography>
                    <Typography variant="h4" color="error.main">
                      {data?.companyWallet.totalUsedByCompany ?? 0}
                </Typography>
                  </Stack>
                  <Avatar
                    sx={{
                      backgroundColor: 'error.main',
                      height: 56,
                      width: 56,
                    }}
                  >
                    <Iconify icon="eva:minus-circle-fill" width={28} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent
                sx={{ paddingTop: '16px', paddingBottom: '16px', paddingX: '20px' }}
              >
                <Stack
                  alignItems="flex-start"
                  direction="row"
                  justifyContent="space-between"
                  spacing={3}
                >
                  <Stack spacing={1}>
                <Tooltip title="Total remaining credits across all agent wallets">
                      <Typography
                        color="text.secondary"
                        variant="overline"
                        sx={{ fontSize: '1rem' }}
                      >
                    Balance available in agents wallet
                  </Typography>
                </Tooltip>
                <Typography variant="h4" color="info.main">
                  {data?.agents?.reduce((sum: number, agent: AgentCreditInfo) => sum + (agent.remainingCredits || 0), 0) ?? 0}
                </Typography>
                  </Stack>
                  <Avatar
                    sx={{
                      backgroundColor: 'info.main',
                      height: 56,
                      width: 56,
                    }}
                  >
                    <HiUsers size={28} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                border: data && data.companyWallet.remainingInWallet < 50 ? '2px solid' : 'none',
                borderColor: 'warning.main',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent
                sx={{ paddingTop: '16px', paddingBottom: '16px', paddingX: '20px' }}
              >
                <Stack
                  alignItems="flex-start"
                  direction="row"
                  justifyContent="space-between"
                  spacing={3}
                >
                  <Stack spacing={1}>
                    <Typography
                      color="text.secondary"
                      variant="overline"
                      sx={{ fontSize: '1rem' }}
                    >
                      Balance available in company wallet
                </Typography>
                <Typography
                  variant="h4"
                      color={data && data.companyWallet.remainingInWallet < 50
                      ? 'warning.main'
                        : 'success.main'}
                >
                  {data?.companyWallet.remainingInWallet || 0}
                </Typography>
                  </Stack>
                  <Avatar
                    sx={{
                      backgroundColor: data && data.companyWallet.remainingInWallet < 50
                        ? 'warning.main'
                        : 'success.main',
                      height: 56,
                      width: 56,
                    }}
                  >
                    {data && data.companyWallet.remainingInWallet < 50 ? (
                      <AiOutlineWarning size={28} />
                    ) : (
                      <FaWallet size={28} />
                    )}
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>

        {/* Active Credit Packages */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Active Credit Packages
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {data?.creditPackages && data.creditPackages.length > 0 ? (
              <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Package Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Credits</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Used</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        <Tooltip title="Total credits allocated to all agents from this package (pool-based system)">
                          <span>Allocated to Agents</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Remaining</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Purchase Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Expires At</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.creditPackages.map((pkg) => {
                      const daysUntilExpiration = getDaysUntilExpiration(pkg.expiresAt);
                      return (
                        <TableRow key={pkg.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {pkg.packageName || 'Unknown Package'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{pkg.totalCredits}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
                              {pkg.usedCredits}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={pkg.allocatedToAgents > 0 ? 'info.main' : 'text.secondary'}
                              sx={{ fontWeight: pkg.allocatedToAgents > 0 ? 500 : 400 }}
                            >
                              {pkg.allocatedToAgents || 0}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={pkg.remainingCredits > 0 ? 'success.main' : 'text.secondary'}
                              sx={{ fontWeight: pkg.remainingCredits > 0 ? 500 : 400 }}
                            >
                              {pkg.remainingCredits}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {moment(pkg.purchasedAt).format('YYYY-MM-DD')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${moment(pkg.expiresAt).format('YYYY-MM-DD')} (${daysUntilExpiration} days)`}
                              color={getExpirationColor(daysUntilExpiration)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={pkg.isActive ? 'Active' : 'Expired'}
                              color={pkg.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                No active credit packages
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Agent Credits Management */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Agent Credits Management
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {data?.agents && data.agents.length > 0 ? (
              <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Agent Name</TableCell>
                      <TableCell align="right">Allocated</TableCell>
                      <TableCell align="right">Used</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.agents.map((agent) => (
                        <TableRow key={agent.agentId} hover>
                        <TableCell>{agent.agentName}</TableCell>
                        <TableCell align="right">{agent.allocatedCredits}</TableCell>
                        <TableCell align="right">{agent.usedCredits}</TableCell>
                        <TableCell align="right">{agent.remainingCredits}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="primary"
                              onClick={() => handleAllocateClick(agent)}
                              title="Allocate Credits"
                            >
                              <Iconify icon="eva:plus-circle-fill" />
                            </IconButton>
                          {agent.remainingCredits > 0 && (
                            <IconButton
                              color="warning"
                              onClick={() => handleReduceClick(agent)}
                              title="Reduce Credits"
                            >
                              <Iconify icon="eva:minus-circle-fill" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                No agents found. Add agents to your company to manage credits.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Allocate Credits Modal */}
        <Dialog
          open={allocateModalOpen}
          onClose={() => {
            setAllocateModalOpen(false);
            allocateFormik.resetForm();
          }}
          maxWidth="sm"
          fullWidth
        >
          <form onSubmit={allocateFormik.handleSubmit}>
            <DialogTitle>Allocate Credits to Agent</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Available Credits: {data?.companyWallet.remainingInWallet || 0}
                </Alert>
                <FormControl
                  fullWidth
                  error={Boolean(allocateFormik.touched.agentId && allocateFormik.errors.agentId)}
                  sx={{ mb: 2 }}
                  disabled={loadingAgents}
                >
                  <InputLabel>Select Agent</InputLabel>
                  <Select
                    name="agentId"
                    value={allocateFormik.values.agentId}
                    label="Select Agent"
                    onChange={allocateFormik.handleChange}
                  >
                    {loadingAgents ? (
                      <MenuItem disabled>Loading agents...</MenuItem>
                    ) : agentsList.filter(a => a.activeState).length === 0 ? (
                      <MenuItem disabled>No active agents available</MenuItem>
                    ) : (
                      agentsList.filter(a => a.activeState).map((agent) => {
                        // Find agent's credit info if available
                        const agentCreditInfo = data?.agents.find((a) => a.agentId === agent.id);
                        const allocatedText = agentCreditInfo
                          ? ` (Current: ${agentCreditInfo.allocatedCredits} allocated)`
                          : ' (No credits allocated)';
                        return (
                          <MenuItem key={agent.id} value={agent.id}>
                            {agent.name} - {agent.email}
                            {allocatedText}
                          </MenuItem>
                        );
                      })
                    )}
                  </Select>
                  {allocateFormik.touched.agentId && allocateFormik.errors.agentId && (
                    <FormHelperText>{allocateFormik.errors.agentId}</FormHelperText>
                  )}
                </FormControl>
                <TextField
                  fullWidth
                  name="credits"
                  label="Credits to Allocate"
                  type="number"
                  value={allocateFormik.values.credits}
                  onChange={allocateFormik.handleChange}
                  error={Boolean(allocateFormik.touched.credits && allocateFormik.errors.credits)}
                  helperText={
                    allocateFormik.touched.credits && allocateFormik.errors.credits
                      ? allocateFormik.errors.credits
                      : `Maximum: ${data?.companyWallet.remainingInWallet || 0} credits`
                  }
                  inputProps={{ min: 1, max: data?.companyWallet.remainingInWallet || 0 }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setAllocateModalOpen(false);
                  allocateFormik.resetForm();
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={allocateFormik.isSubmitting}
              >
                Allocate
              </LoadingButton>
            </DialogActions>
          </form>
        </Dialog>

        {/* Reduce Credits Modal */}
        <Dialog
          open={reduceModalOpen}
          onClose={() => {
            setReduceModalOpen(false);
            reduceFormik.resetForm();
            setSelectedAgent(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <form onSubmit={reduceFormik.handleSubmit}>
            <DialogTitle>Reduce Credits from Agent</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                {selectedAgent && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Agent: {selectedAgent.agentName}
                    <br />
                    Allocated: {selectedAgent.allocatedCredits} | Used: {selectedAgent.usedCredits}{' '}
                    | Remaining: {selectedAgent.remainingCredits}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  name="credits"
                  label="Credits to Reduce"
                  type="number"
                  value={reduceFormik.values.credits}
                  onChange={reduceFormik.handleChange}
                  error={Boolean(reduceFormik.touched.credits && reduceFormik.errors.credits)}
                  helperText={
                    reduceFormik.touched.credits && reduceFormik.errors.credits
                      ? reduceFormik.errors.credits
                      : selectedAgent
                      ? `Maximum: ${selectedAgent.remainingCredits} credits (cannot reduce below used credits)`
                      : ''
                  }
                  inputProps={{
                    min: 1,
                    max: selectedAgent ? selectedAgent.remainingCredits : 0,
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setReduceModalOpen(false);
                  reduceFormik.resetForm();
                  setSelectedAgent(null);
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                color="warning"
                loading={reduceFormik.isSubmitting}
              >
                Reduce
              </LoadingButton>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Page>
  );
};

export default CreditManagement;

